import { ReferralType, ScamLevelType } from '../constants';
import type { BiEvent } from './base';

export interface MixinViewComponent {
  view: string;
  component?: string;
}

export interface MixinItemIdAndTypeId {
  itemid: number;
  typeid: number;
}

export interface MixinSection {
  sectionname: string;
}

export interface PageViewContextData
  extends Partial<MixinItemIdAndTypeId>,
    MixinViewComponent {}

// #region 0-99
export interface PerfMetricsBiEvent extends BiEvent {
  /** project version */
  appinf: string;
  /** when we sent the data */
  beat: 'fcp' | 'lcp' | 'inp';

  // Debug helpers
  debug_info?: string;

  // Network Timing
  /** fetchTime */
  nt_ft?: number;
  /** workerTime */
  nt_wt?: number;
  /** totalTime */
  nt_tt?: number;
  /** downloadTime */
  nt_dt?: number;
  /** timeToFirstByte */
  nt_ttfb?: number;
  /** headerSize */
  nt_hs?: number;
  /** dnsLookupTime */
  nt_dlt?: number;

  // Network Info
  /** downlink */
  ni_dl?: number;
  /** effectiveType */
  ni_et?: string;
  /** rtt (dunno) */
  ni_rtt?: number;
  /** saveData (bi support bools??) */
  ni_sd?: 0 | 1;

  // metrics fcp beat
  /** firstPaint */
  p_fp?: number;
  /** firstContentfulPaint */
  p_fcp?: number;

  // metrics lcp beat
  /** firstInputDelay */
  p_fid?: number;
  /** largestContentfulPaint */
  p_lcp?: number;

  // metrics for INP beat
  p_inp?: number;
}

export interface DefaultBiEvent extends BiEvent {
  evid: 2;
  /** a string to uniquely identify this particular clickable feature */
  click_info: string;
  sectionname?: string;
  widgetname?: string;
  typeid?: number;
  itemid?: number;
}

export interface BotDetected extends BiEvent {
  evid: 3;
  bot_kind: string;
  bot_trap: string;
}

export interface PageViewBiEventDeviationPhase2
  extends Omit<PageViewBiEvent, 'evid'> {
  evid: 8;
}

export interface PageViewBiEventNg extends Omit<PageViewBiEvent, 'evid'> {
  evid: 9;
}

export interface SubmitContentBiEvent extends BiEvent {
  evid: 11;
  group_exclusive?: boolean;
  typeid?: number;
  itemid?: number;
}

export interface StudioBatchUpdateBiEvent extends BiEvent {
  evid: 12;
  update_type: string;
  items_count: number;
}

export interface StudioSavePresetsBiEvent extends BiEvent {
  evid: 13;
  presetid?: number;
  previous_presetid?: number;
  is_preset_created?: 0 | 1;
}

export interface StudioSearchBiEvent extends BiEvent {
  evid: 14;
  search_terms?: string;
}

export interface ManageInStudioClickBiEvent extends BiEvent {
  evid: 15;
  link_name: string; // the clickable text
  link_url: string; // url the click leads to
  sectionname: string; // section of page where click happens
  widgetname?: string;
}

export interface OpenUploadBiEvent extends BiEvent {
  evid: 16;
  sectionname: string;
}

export interface SaveDraftClickBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 17;
}

export interface DownloadAllClickBiEvent
  extends BiEvent,
    MixinViewComponent,
    MixinSection,
    MixinItemIdAndTypeId {
  evid: 18;
}

export interface OpenStudioPresetsBiEvent extends BiEvent {
  evid: 19;
  sectionname?: string;
}

export interface ClickReportButtonBiEvent extends BiEvent {
  evid: 20;
  deviationid: number;
  /** The version of the reporting flow that will be seen by the user */
  modal_version?: number;
}

export interface ClickReportChoseTypeBiEvent extends BiEvent {
  evid: 21;
  type_chosen: string;
  deviationid: number;
}

export interface ClickReportNextBiEvent extends BiEvent {
  evid: 22;
  report_type: string;
  deviationid: number;
}

export interface ClickReportCancelBiEvent extends BiEvent {
  evid: 25;
  button: string;
  deviationid: number;
}

export interface ClickReportBackBiEvent extends BiEvent {
  evid: 26;
  deviationid: number;
}

export interface ClickReportSubmitBiEvent extends BiEvent {
  evid: 29;
  deviationid: number;
  type_chosen: string;
}

export interface SubmitAddImageClickBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 30;
  sectionname?: string;
  rank?: number;
}

export interface SubmitReplaceSourceImageOpenBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 31;
}

export interface SubmitManageFilesOpenBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 32;
}

export interface SubmitDeleteMultiImageFileClickBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 35;
  sectionname?: string;
  rank?: number;
}

export interface ScheduleOpenClickBiEvent extends BiEvent {
  evid: 36;
  sectionname?: string;
  typeid?: number;
  itemid?: number;
  action?: 'schedule' | 'reschedule';
}

export interface AddNewFolderClickBiEvent {
  evid: 40;
  typeid: number;
  /**
   * galleryid/stashid of a parent gallery/folder
   */
  itemid?: number;
  sectionname?: string;
  widgetname?: string;
}

export interface MoveToFolderClickBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId>,
    Partial<MixinSection> {
  evid: 41;
  action: string;
  folder_id: number;
  folder_typeid: number;
}

export interface PostFeedViewBiEvent extends BiEvent {
  evid: 95;
  itemid: number;
  typeid: number;
  offset: number;
  /** metadata in JSON format */
  metadata: string;
}

export interface ScrollToLoadNextFolderBiEvent extends BiEvent {
  evid: 96;
  itemid: number;
  typeid: number;
  next_itemid: number;
  next_typeid: number;
}

export interface BackToTopBiEvent extends BiEvent {
  evid: 97;
  itemid?: number;
  typeid?: number;
  offset?: number;
}

export interface ScrollToLoadMoreBiEvent extends BiEvent {
  evid: 98;
  itemid?: number;
  typeid?: number;
  offset?: number;
}

// This is the default Bi event interface for da-user-profile widgets
// these properties are automatically provided via BiLoggerContextProvider in WidgetBox
export interface WidgetBiEvent extends BiEvent {
  sectionname?: string;
  widgetname?: string;
  widgetzone?: string;
}

export interface PageViewBiEvent extends BiEvent {
  evid: 99;
  view: string;
  component?: string;
  /** this is the active folder on notification and watch pages */
  feed_mode?: 'feed' | 'saved' | 'removed';
  is_duper?: 0 | 1;
  itemid?: number;
  typeid?: number;
  /** view mode for feedback pageviews */
  view_mode?: 'simple' | 'advanced' | 'grid' | 'lists' | 'stacked';
  /** type of view opened in stats */
  time_period?: 'P7D' | 'P28D' | 'P365D';
  /** non core view in user stats, 1 if user does not have core */
  non_core_view?: 0 | 1;
  /** 1 if user don't have stats ready */
  is_empty_state_view?: 0 | 1;
  /** metadata in JSON format */
  metadata?: string;
  /** defines the sort type selected, for e.g. recommended for you/recent/popular etc */
  sort_type?: string;
  /** view type if a deviation is blocked/locked */
  content_view_status?: string;
  screen_width?: number;
  screen_height?: number;
  /** Dreamup related. The number of prompts remaining in the user's monthly allotment. */
  num_of_prompts?: number;
}

// #endregion
// #region 100-199
export interface ItemClickBiEvent extends BiEvent, Partial<MixinViewComponent> {
  evid: 100;
  sectionid?: number;
  sectionname?: string;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  notification_sender_id?: string;
  notification_type?: string;
  notification_rank?: number;
  notification_item_id?: number;
  notification_type_id?: number;

  widgetname?: string;
  rank?: number;
  creatorid?: string;
  itemid?: number;
  typeid?: number;
  link_url?: string;
  sort_type?: string;
  reason_shown?: string;
  content_view_status?: string;
  page_typeid?: number;
  page_itemid?: number;
}

export interface UserNameClickBiEvent extends BiEvent {
  evid: 101;
  itemid: number;
  typeid: number;
  creatorid?: string;
  sectionname?: string;
  sectionid?: number;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  rank?: number;
  sort_type?: string;
  reason_shown?: string;
  widgetname?: string;

  attribution?: null;
  notification_sender_id?: string;
  notification_type?: string;
}

export interface ThumbFaveBiEvent extends BiEvent {
  evid: 102;
  itemid: number;
  typeid: number;
  creatorid?: string;
  sectionname?: string;
  sectionid?: number;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  rank?: number;
  sort_type?: string;
  reason_shown?: string;
  widgetname?: string;
}

export interface ThumbClickCommentBiEvent extends BiEvent {
  evid: 105;
  itemid: number;
  typeid: number;
  creatorid: string;
  sectionname?: string;
  sectionid?: number;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  rank?: number;
  sort_type?: string;
  reason_shown?: string;
  widgetname?: string;
}

export interface SeeAllClickBiEvent extends BiEvent {
  evid: 106;
  attribution?: string;
  creatorid?: string;
  typeid?: number;
  itemid?: number;
  sectionid?: number;
  sectionname?: string;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  widgetname?: string;
  rank?: number;
  sort_type?: string;
  reason_shown?: string;
}

export interface UserNameHoverToOpenBiEvent extends BiEvent {
  evid: 107;
  attribution?: string;
  creatorid?: string;
  typeid?: number;
  itemid?: number;
  sectionid?: number;
  sectionname?: string;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  widgetname?: string;
  rank?: number;
  sort_type?: string;
  reason_shown?: string;
}

export interface CarouselArrowClickBiEvent extends BiEvent {
  evid: 108;
  typeid?: number;
  itemid?: number;
  direction?: string;
  scroll_pct?: number;
  sectionid?: number;
  sectionname?: string;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  widgetname?: string;
}

export interface ThumbClickUnfaveBiEvent extends BiEvent {
  evid: 109;
  itemid: number;
  typeid: number;
  creatorid?: string;
  sectionname?: string;
  sectionid?: number;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  rank?: number;
  sort_type?: string;
  reason_shown?: string;
  widgetname?: string;
}

export interface ShowLessLikeThisBiEvent extends BiEvent {
  evid: 110;
  itemid: number;
  typeid: number;
  creatorid: string;
  sectionname?: string;
  sectionid?: number;
  section_typeid?: number;
  rank?: number;
  sort_type?: string;
  reason_shown?: string;
}

export interface UserSymbolClickBiEvent extends BiEvent {
  evid: 111;
  symbol: string;
}

export interface UserSymbolLearnMoreClickBiEvent extends BiEvent {
  evid: 112;
  link_name: string;
  link_url: string;
  rank?: number;
}

export interface PageNavigationClickBiEvent extends BiEvent {
  evid: 113;
  direction: string;
  thumbs_per_page?: number;
}

export interface TopicsViewallClickBiEvent extends BiEvent {
  evid: 114;
  is_expanding: number;
}

export interface PollVoteBiEvent extends BiEvent {
  evid: 115;
  pollid: number;
  rank?: number;
  sort_type?: string;
}

export interface ReadMoreClickBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 116;
  creatorid: string;
}

export interface PostFromFeedBiEvent extends BiEvent {
  evid: 117;
  is_from_textbox: number;
  typeid: number;
  sort_type?: string;
  sectionname?: string;
}

export interface SwitchPostTypeBiEvent extends BiEvent {
  evid: 118;
  switch_to: string;
}

export interface NextPostBiEvent extends BiEvent {
  evid: 119;
  itemid: number;
  typeid: number;
  creatorid?: string;
  rank?: number;
  sort_type?: string;
}

export interface ContentNavbarClickBiEvent extends BiEvent {
  evid: 120;
  link_name: string;
  indicator?: number;
}

export interface SortTypeSelectBiEvent extends BiEvent {
  evid: 122;
  menu_selection: string;
  dropdown_type: string;
}

export interface MoreLikeThisClickBiEvent extends BiEvent {
  evid: 126;
  itemid: number;
  typeid: number;
  creatorid?: string;
  sectionname?: string;
}

export interface SearchTagClickBiEvent extends BiEvent {
  evid: 140;
  tag_text: string;
  is_selected: number;
  link_url: string;
}

export interface NetworkbarNavClickBiEvent
  extends BiEvent,
    Partial<MixinSection>,
    Partial<MixinItemIdAndTypeId> {
  evid: 160;
  indicator: number;
  is_expanded: number;
  rank?: number;
}

export interface NetworkbarMenuIconBiEvent extends BiEvent {
  evid: 161;
}

export interface Networkbar3DotsOpenBiEvent
  extends BiEvent,
    Partial<MixinSection>,
    Partial<MixinItemIdAndTypeId> {
  evid: 162;
  indicator: number;
  rank?: number;
}

export interface Networkbar3DotsSelectBiEvent
  extends BiEvent,
    Partial<MixinSection>,
    Partial<MixinItemIdAndTypeId> {
  evid: 163;
  indicator: number;
  menu_item?: string;
  rank?: number;
}

export interface NetworkbarSectionExpandCollapseEvent extends BiEvent {
  evid: 164;
  indicator: number;
  is_expanding: number;
}

export interface ViewGroupNotificationEvent
  extends BiEvent,
    MixinItemIdAndTypeId,
    MixinSection {
  evid: 165;
  groupid: string;
}

export interface CommentActionEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 180;
  commentid: number;
  commenterid: string;
}

export interface CilckReplyToCommentBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 181;
  commentid: number;
  commenterid: string;
}

export interface LoadMoreCommentsClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 182;
  is_load_previous?: number;
}

export interface ClickOnCommentsToStartTypingBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 183;
}

export interface CommentButtonClickBiEvent extends BiEvent {
  evid: 184;
  itemid: number;
  typeid: number;
  is_comment_thread: 0 | 1;
  is_reply: 0 | 1;
  is_sticky: 0 | 1;
  notification_type?: string;
  has_exclusive?: 0 | 1;
}

export interface ClickViewReplyToCommentBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 185;
  commentid: number;
  commenterid: string;
}

export interface ClickCommentLikeBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 186;
  commentid: number;
  commenterid: string;
  is_liking: number;
}

export interface CommentEditClickBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 187;
  commentid: number;
}

export interface CommentDeletionUndoBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 188;
  is_comment_thread: number;
  commentid: number;
  commenterid: string;
}

export interface CommentCancelClickBiEvent extends BiEvent {
  evid: 189;
  itemid: number;
  typeid: number;
  is_comment_thread: 0 | 1;
  is_sticky: 0 | 1;
}

export interface CommentSuggestionsViewBiEvent extends BiEvent {
  evid: 190;
  itemid: number;
  typeid: number;
  creatorid: string;
  comment_suggestions: string;
}

export interface CommentSuggestionClickBiEvent extends BiEvent {
  evid: 191;
  itemid: number;
  typeid: number;
  creatorid: string;
  comment_suggestions: string;
  selected_suggestions: string;
  rank: number;
  is_shown: 0 | 1;
  is_surprise_me: 0 | 1;
}

export interface CommentSuggestionsUnhideBiEvent extends BiEvent {
  evid: 192;
  itemid: number;
  typeid: number;
  creatorid: string;
  comment_suggestions: string;
  is_displayed: 0 | 1;
}

export interface EditorButtonClickBiEvent extends BiEvent, MixinViewComponent {
  evid: 193;
  itemid?: number;
  typeid?: number;
  preset: string;
  feature: string;
}

export interface AvatarOpenBiEvent extends BiEvent {
  evid: 194;
  notification_type?: string;
  notification_message_id?: string;
  typeid?: number;
  itemid?: number;
  is_comment_thread?: 1 | 0 | null;
  has_exclusive: 0 | 1;
}

export interface ApplyAvatarBiEvent extends BiEvent {
  evid: 195;
  notification_type?: string;
  notification_message_id?: string;
  typeid?: number;
  itemid?: number;
  is_comment_thread?: 1 | 0 | null;
  has_exclusive: 0 | 1;
  new_exclusiveid: number | null;
}

// #endregion
// #region 200-299
export interface ProfileNavBarBiEvent
  extends BiEvent,
    MixinViewComponent,
    MixinItemIdAndTypeId {
  evid: 200;
  link_name: string;
}

export interface LoginScreenShowBiEvent extends BiEvent {
  evid: 201;
  user_name_challenge_mode: any;
}

export interface LoginScreenSubmitBiEvent extends BiEvent {
  evid: 202;
  user_name_challenge_mode: any;
}

export interface LoginErrorBiEvent extends BiEvent {
  evid: 203;
  error_message: string;
  error_type: any;
  user_name_challenge_mode: any;
}

export interface LoginRememberClickBiEvent extends BiEvent {
  evid: 204;
  user_name_challenge_mode: any;
  new_state: number;
}

export interface LoginForgotPasswordClickBiEvent extends BiEvent {
  evid: 205;
  user_name_challenge_mode: any;
}

export interface TermsClickBiEvent extends BiEvent {
  evid: 206;
  user_name_challenge_mode: any;
}

export interface PolicyClickBiEvent extends BiEvent {
  evid: 207;
  user_name_challenge_mode: any;
}

export interface LoginSuccessBiEvent extends BiEvent {
  evid: 211;
  is_new_user: any;
  auth_mode: string;
}

export interface WelcomeScreenShowBiEvent extends BiEvent {
  evid: 216;
  auth_mode: string;
  absent_days: any;
  is_new_user: any;
}

export interface WelcomeScreenCloseBiEvent extends BiEvent {
  evid: 217;
  auth_mode: string;
  is_new_user: any;
}

export interface WelcomeStartClickBiEvent extends BiEvent {
  evid: 218;
  auth_mode: string;
  is_new_user: any;
}

export interface WelcomeChangeEmailClickBiEvent extends BiEvent {
  evid: 219;
  auth_mode: string;
  is_new_user: any;
}

export interface IntentShowBiEvent extends BiEvent {
  evid: 220;
  auth_mode: string;
}

export interface IntentOptionToggleBiEvent extends BiEvent {
  evid: 221;
  auth_mode: string;
  new_state: number;
  option: string;
}

export interface IntentSubmitBiEvent extends BiEvent {
  evid: 226;
  auth_mode: string;
}

export interface LoginCaptchaShowBiEvent extends BiEvent {
  evid: 227;
}

export interface AlmostDoneShowBiEvent extends BiEvent {
  evid: 228;
  auth_mode: string;
}

export interface AlmostDoneResendClickBiEvent extends BiEvent {
  evid: 229;
  auth_mode: string;
}

export interface AlmostDoneChangeAddressClickBiEvent extends BiEvent {
  evid: 230;
  auth_mode: string;
}

export interface AlmostDoneCloseBiEvent extends BiEvent {
  evid: 231;
  auth_mode: string;
}

export interface VerificationSuccessfulBrowseArtClickBiEvent extends BiEvent {
  evid: 234;
}

export interface TopicsOnboardingShowBiEvent
  extends BiEvent,
    MixinViewComponent {
  evid: 235;
  auth_mode: string;
}

export interface TopicsOnboardingSelecBiEventt
  extends BiEvent,
    MixinItemIdAndTypeId,
    MixinViewComponent {
  evid: 236;
  topic: string;
  rank: number;
  is_selected: number;
  deviationid: number;
}

export interface TopicsOnboardingDoneBiEvent
  extends BiEvent,
    MixinViewComponent {
  evid: 237;
  auth_mode: string;
}

export interface SignupScreenClickContinueBiEvent extends BiEvent {
  evid: 238;
  page_number: number;
  auth_mode?: string;
}

export interface TfaScreenShownBiEvent extends BiEvent {
  evid: 239;
}

export interface RedirectToLoginBiEvent extends BiEvent {
  evid: 240;
  referral_info?: string;
}

export interface ClickJoinBiEvent extends BiEvent {
  evid: 241;
  referral_info: ReferralType;
}

export interface ClickLoginBiEvent extends BiEvent {
  evid: 242;
}

export interface SignupScreenShowBiEvent extends BiEvent {
  evid: 243;
}

export interface LoginScreenSignupClickBiEvent extends BiEvent {
  evid: 244;
}

export interface LoginOnSignupClickBiEvent extends BiEvent {
  evid: 245;
  referral_info?: ReferralType;
}

export interface SignupOnSignupClickBiEvent extends BiEvent {
  evid: 247;
  auth_mode?: string;
}

export interface SignupSuccessBiEvent extends BiEvent {
  evid: 248;
  auth_mode: string;
}

export interface SignupErrorBiEvent extends BiEvent {
  evid: 249;
  error_message: string;
}

export interface ProfileLoadMoreClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 265;
  widgetname?: string;
}

export interface ProfilePostFeedDropdownBiEvent extends BiEvent {
  evid: 266;
  menu_item: string;
}

export interface CustomBoxPreviewBiEvent extends BiEvent {
  evid: 268;
  widgetname: string;
}

export interface CustomBoxTemplateDoneClickBiEvent extends WidgetBiEvent {
  evid: 269;
  template_id?: number;
}

export interface WidgetRemoveConfirmBiEvent extends WidgetBiEvent {
  evid: 270;
}

export interface WidgetEditSaveBiEvent extends WidgetBiEvent {
  evid: 271;
  itemid: number;
  typeid: number;
  display_type: string;
  sort_order: string;
  gallery_title: string;
  gallery_type: string;
  widgetname: string;
  widgetzone: string;
}

export interface GivePointsClickBiEvent extends BiEvent {
  evid: 272;
  points_total: number;
  sent_personal_message: number;
  widgetname: string;
}

export interface SendNoteClickBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 273;
  friend_id: string;
  sectionname?: string;
  recipientid?: string;
}

export interface GiftClickBiEvent extends BiEvent {
  evid: 274;
  recipientid: string;
}

export interface GiftSelectClickBiEvent extends BiEvent {
  evid: 275;
  recipientid?: string;
  link_url?: string;
  menu_item_name?: string;
}

export interface LlamaFarmGiftClickBiEvent extends GiftSelectClickBiEvent {
  evid: 275;
  menu_item_name?: string;
  widgetname: string;
}

export interface ProfileCoverClickBiEvent extends BiEvent {
  evid: 276;
  is_update: number;
}

export interface ProfileWatchIntroBiEvent extends BiEvent {
  evid: 277;
}

export interface EditWidgetClickBiEvent extends BiEvent {
  evid: 278;
  widgetname: string;
  widgetzone: string;
}

export interface RemoveWidgetClickBiEvent extends BiEvent {
  evid: 280;
  widgetname: string;
  widgetzone: string;
}

export interface MoveWidgetClickBiEvent extends BiEvent {
  evid: 281;
  widgetname: string;
  widgetzone: string;
  direction: 'up' | 'down' | 'left' | 'right';
}

export interface AddWidgetClickBiEvent extends BiEvent {
  evid: 282;
}

export interface AddWidgetBiEvent extends BiEvent {
  evid: 283;
  widgetname: string;
  widgetzone: string;
}

export interface AddNewContentClickBiEvent extends BiEvent {
  evid: 284;
  widgetname: string;
}

export interface DonatePointsClickBiEvent extends BiEvent {
  evid: 286;
  recipientid: string;
}

export interface ProfileGalleryViewModeClickBiEvent extends BiEvent {
  evid: 288;
  view_mode: string;
}

export interface GallerySearchClickBiEvent extends BiEvent {
  evid: 289;
}

export interface ProfileSubNavBarBiEvent extends BiEvent {
  evid: 290;
  menu_item: string;
}

export interface ProfileAboutSidebarBiEvent extends BiEvent {
  evid: 291;
}

export interface ProfileSubNavBarToggleBiEvent extends BiEvent {
  evid: 292;
}

export interface ProfileOrderCommissionBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 293;
  points: number;
  creatorid: string;
}

export interface CustomBoxTemplateClickEvent extends WidgetBiEvent {
  evid: 294;
  template_id?: number;
}

export interface TemplatePickerSubmitBiEvent extends WidgetBiEvent {
  evid: 295;
  template_id?: number;
  items_list?: string[];
}

export interface DonationPoolSetupConfirmBiEvent extends BiEvent {
  evid: 296;
  donation_goal_points: string;
}

export interface ClickSocialLinkBiEvent extends BiEvent {
  evid: 297;
  typeid: number;
  itemid: number;
  widgetname: string;
  social_share: string;
}

export interface GallectionSearchSubmitBiEvent extends BiEvent {
  evid: 299;
  search_text: string;
}

// #endregion
// #region 300-399
export interface NavigationArrowClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 300;
  direction: string;
  next_itemid?: number;
}

export interface TheatreModeBiEvent extends BiEvent {
  evid: 301;
}

export interface FullscreenModeBiEvent extends BiEvent {
  evid: 302;
}

export interface ZoomModeBiEvent extends BiEvent {
  evid: 303;
  view: string;
  component: string;
  typeid: number;
  deviationid: number;
  viewmode: string;
}

export interface BackToClickBiEvent extends BiEvent {
  evid: 304;
  link_url?: string;
  itemid?: number;
  typeid?: number;
  next_itemid?: number;
  next_typeid?: number;
}

export interface ShowDeviationAnywayClickBiEvent extends BiEvent {
  evid: 305;
  itemid?: number;
  typeid?: number;
}

export interface OpenBrowsingModeClickBiEvent extends BiEvent {
  evid: 306;
  itemid?: number;
  typeid?: number;
}

export interface SaveBrowsingModeClickBiEvent extends BiEvent {
  evid: 307;
  view_mode?: string;
}

export interface SaveAIBrowsingModeClickBiEvent extends BiEvent {
  evid: 308;
  view_mode?: string;
}

export interface SetMultiImageDisplayModeClickBiEvent extends BiEvent {
  evid: 309;
  deviationid: number;
  view_mode: string;
}

export interface MultiImageFileClickBiEvent extends BiEvent {
  evid: 310;
  deviationid: number;
  fileid?: number;
  rank?: number;
  usagetype?: number;
}

export interface GiveBadgeAwardBiEvent extends BiEvent {
  evid: 320;
  sectionname: string;
  itemid?: number;
  typeid?: number;
  badge_type?: string;
  badge_typeid?: number;
}

export interface BadgeAwardClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId,
    MixinSection {
  evid: 321;
  badge_type: string;
  badge_typeid: number;
  selected_tags?: string;
  num_tags?: number;
}

export interface BadgeTypeSelectBiEvent extends BiEvent {
  evid: 322;
  badge_typeid: number;
  badge_type: string;
  sectiontype?: string;
  sectionname?: string;
  typeid?: number;
  itemid?: number;
}

export interface BadgeStatisticsClickBiEvent extends BiEvent {
  evid: 323;
  sectionname: string;
  typeid: number;
  itemid: number;
}

export interface BadgeHelpClickBiEvent extends BiEvent {
  evid: 324;
  sectionname: string;
  typeid: number;
  itemid: number;
}

export interface ReorderBadgeBiEvent extends BiEvent {
  evid: 325;
  typeid_list: number[];
  badge_typeid: number;
  sectionname?: string;
}

export interface AddToCollectionDoneBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId,
    MixinSection {
  evid: 331;
  creatorid: string;
  collectionid: number;
  num_collections: number;
}

export interface PopupClickAddToCollectionBiEvent
  extends BiEvent,
    MixinSection {
  evid: 333;
}

export interface PopupClickAddToPrivateCollectionBiEvent extends BiEvent {
  evid: 334;
  typeid: number;
  itemid: number;
  sectionname?: string;
  creatorid?: string;
}

export interface AddToPrivateCollectionDoneBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId,
    Partial<MixinSection> {
  evid: 335;
  creatorid: string;
  collectionid?: number;
  num_collections: number;
}

export interface AddToGroupClickBiEvent extends BiEvent {
  evid: 340;
  deviationid: number;
  sectionname?: string;
  source_type?: string;
}

export interface AddToGroupFolderSelectBiEvent extends BiEvent {
  evid: 342;
  typeid: number;
  itemid: number;
  groupid: string;
  deviationid: number;
  source_type?: string;
}

export interface AddToGroupSelectBiEvent extends BiEvent {
  evid: 341;
  groupid: string;
  deviationid: number;
  source_type?: string;
}

export interface BuyPremiumDownloadClickBiEvent extends BiEvent {
  evid: 349;
  sectionname: 'action_bar' | 'side_rail';
  itemprice: string;
  itemid: number;
  typeid: number;
}

export interface ClickDownloadButtonBiEvent extends BiEvent {
  evid: 350;
  sectionname?: string;
  itemid?: number;
  typeid?: number;
  deviationid: number;
  ispaiddownload?: 0 | 1;
}

export interface DownloadNowClickBiEvent extends BiEvent {
  evid: 351;
  deviationid: number;
}

export interface StartDownloadClickBiEvent extends BiEvent {
  evid: 352;
  deviationid: number;
}

export interface SellYourArtClickBiEvent extends BiEvent {
  evid: 353;
  deviationid?: number;
  sectionname?: string;
  items_count?: number;
  deviationid_list?: string; // comma-delineated list of deviationids
}

export interface EditSaleBiEvent extends BiEvent {
  evid: 354;
  deviationid: number;
  sectionname?: string;
}

export interface ClickShareBiEvent extends BiEvent {
  evid: 355;
  itemid: number;
  typeid: number;
  deviationid?: number;
}

export interface ClickSocialShareBiEvent extends BiEvent {
  evid: 356;
  social_share: string;
  itemid: number;
  typeid: number;
  deviationid?: number;
  sectionname?: string;
  source_type?: string;
}

export interface ClickCopyBiEvent extends BiEvent {
  evid: 357;
  itemid?: number;
  typeid?: number;
  view?: string;
  component?: string;
  sectionname?: string;
}

export interface UnlistItemOpenBiEvent extends BiEvent {
  evid: 358;
  itemid?: number;
  typeid?: number;
  sectionname?: string;
}

export interface UnlistItemConfirmBiEvent extends BiEvent {
  evid: 359;
  itemid?: number;
  typeid?: number;
  sectionname?: string;
}

export interface BuyClickBiEvent extends BiEvent {
  evid: 360;
}

export interface GalleryPurchaseBiEvent extends BiEvent {
  evid: 361;
  typeid: number;
  itemid: number;
  galleryid: number;
  authorid?: string;
  points?: number;
  rank?: number;
  reason_shown?: string;
  section_typeid?: number;
  sectionid?: number;
  sectionname?: string;
  sectionrank?: number;
  sectiontype?: string;
  sort_type?: string;
  widgetname?: string;
}

export interface BuyInPopupStoreBiEvent extends BiEvent {
  evid: 362;
}

export interface GoToGalleryBiEvent extends BiEvent {
  evid: 363;
  galleryid: number;
  typeid?: number;
  itemid?: number;
  authorid: string;
  folderid?: number;
  notification_type?: string;
}

export interface ExternalLinkClick
  extends BiEvent,
    MixinItemIdAndTypeId,
    MixinSection {
  evid: 364;
  link_url: string;
  message_id?: string;
  scam_level?: ScamLevelType;
}

export interface ExternalLinkClickConfirm
  extends BiEvent,
    MixinItemIdAndTypeId,
    MixinSection {
  evid: 365;
  link_url: string;
  message_id?: string;
  scam_level?: ScamLevelType;
}

export interface ChangeBackgroundClickBiEvent extends BiEvent {
  evid: 370;
  background_name?: string;
  background_id?: number;
  typeid: number;
  itemid?: number;
  core_only: 1 | 0;
}

export interface DeviationEditClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 371;
  sectionname?: string;
}

export interface DeviationDeleteClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 372;
}

export interface SelectBackgroundBiEvent extends BiEvent {
  evid: 373;
  background_name?: string;
  background_id?: number;
  typeid: number;
  itemid?: number;
  core_only: 1 | 0;
}

export interface CloseBackgroundSelectionModalBiEvent extends BiEvent {
  evid: 374;
  background_name?: string;
  background_id?: number;
  typeid: number;
  itemid?: number;
  core_only: 1 | 0;
}

export interface BuyCoreButtonBiEvent extends BiEvent {
  evid: 375;
  referral_info: ReferralType;
  typeid?: number;
  itemid?: number;
  background_id?: number;
}

export interface DeleteDeviationConfirmClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 376;
}

export interface MarketplaceFiltersBiEvent extends BiEvent {
  evid: 377;
  tag?: string;
  watched?: boolean;
  dd?: boolean;
  discord?: boolean;
  min_price?: string;
  max_price?: string;
  resale_allowed?: boolean;
}

export interface WatchAuthorBiEvent extends BiEvent {
  evid: 380;
  friendid?: string;
  is_watching?: 0 | 1;
  itemid?: number;
  typeid?: number;
  rank?: number;
  sectionid?: number;
  sectionname?: string;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  sort_type?: string;
  reason_shown?: string;
  notification_type?: string;
}

export interface JoinGroupBiEvent extends BiEvent, Partial<MixinSection> {
  evid: 381;
  groupid: string;
}

export interface UnwatchConfirmClickBiEvent extends BiEvent {
  evid: 385;
  friendid?: string;
  itemid?: number;
  typeid?: number;
  rank?: number;
  sectionid?: number;
  sectionname?: string;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  sort_type?: string;
  reason_shown?: string;
  notification_type?: string;
}

export interface TagClickBiEvent extends BiEvent {
  evid: 391;
  tag_text: string;
  link_url: string;
  tagid?: number;
  typeid?: number;
  itemid?: number;
}

export interface SubjectClickBiEvent extends BiEvent {
  evid: 392;
  subject_name: string;
  subject_type: string;
}

export interface LocationClickBiEvent extends BiEvent {
  evid: 393;
  location_name: string;
}

export interface NotificationEduVideoBiEvent extends BiEvent {
  evid: 394;
}

// #endregion
// #region 400-499
export interface WatchSectionSelectBiEvent extends BiEvent {
  evid: 400;
  sectionname?: string;
  is_from_navbar?: 0 | 1;
}

export interface ChangeFeedModeBiEvent extends BiEvent {
  evid: 401;
  feed_mode: 'feed' | 'saved' | 'removed';
  previous_feed_mode: 'feed' | 'saved' | 'removed';
  itemid?: number;
  typeid?: number;
}

export interface UserOrGroupFeedSelectBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 402;
  indicator?: number;
  is_birthday?: number;
  feed_mode?: string;
  sectionname?: string;
}

export interface FeedSortOrderSelectBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 403;
  feed_mode?: string;
  sort_order: string;
}

export interface ManageWatchListBiEvent extends BiEvent {
  evid: 404;
}

export interface WatchItemRemoveClickBiEvent extends BiEvent {
  evid: 405;
  itemid?: number;
  typeid?: number;
  rank?: number;
  button_name?: string;
  notification_type?: string;
  notification_message_id?: string;
  sectionname?: string;
  sectionid?: number;
  section_typeid?: number;
  sectiontype?: string;
  items_selected_count?: number;
  is_grouped?: 0 | 1;
}

export interface WatchSuggestionRemoveClickBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId,
    MixinSection {
  evid: 406;
  items_selected_count: number;
}

export interface WatchFilterTabSelectionBiEvent extends BiEvent {
  evid: 407;
  typeid: number;
}

export interface WatchFilterSearchIconClickBiEvent extends BiEvent {
  evid: 408;
  typeid: number;
}

export interface WatchFilterCloseSearchClickBiEvent extends BiEvent {
  evid: 409;
  typeid: number;
}

export interface WatchFilterSearchBiEvent extends BiEvent {
  evid: 410;
  typeid: number;
  search_text: string;
}

export interface WatchFilterSelectAllClickBiEvent extends BiEvent {
  evid: 411;
  select_all_type: string;
}

export interface WatchGroupByArtistClickBiEvent extends BiEvent {
  evid: 412;
  is_grouped: number;
}

export interface WatchViewAllByArtistBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 413;
}

export interface WatchDeviationSelectBiEvent extends BiEvent {
  evid: 414;
  feed_mode?: 'feed' | 'saved' | 'removed';
  sectionname?: string;
  is_grouped?: 0 | 1;
  typeid?: number;
  itemid?: number;
  rank?: number;
  groupid?: string | number;
  notification_type?: string;
  notification_sender_id?: string;
  items_selected_count?: number;
}

export interface WatchDeviationDeselectBiEvent extends BiEvent {
  evid: 415;
  feed_mode?: 'feed' | 'saved' | 'removed';
  sectionname?: string;
  is_grouped?: 0 | 1;
  typeid?: number;
  itemid?: number;
  rank?: number;
  groupid?: string | number;
  notification_type?: string;
  notification_sender_id?: string;
  items_selected_count?: number;
}

export interface WatchConfirmRemovalBiEvent extends BiEvent {
  evid: 416;
  is_grouped: 0 | 1;
  removal_confirmed: 0 | 1;
  typeid?: number;
  itemid?: number;
  groupid?: string | number;
  rank?: number;
  sectionname?: string;
  section_typeid?: number;
  sectionid?: number;
  notification_type?: string;
  items_selected_count?: number;
  notification_sender_id?: string;
  feed_mode?: 'feed' | 'saved' | 'removed';
}

export interface MoreMenuClickBiEvent extends BiEvent {
  evid: 417;
  sectionname?: string;
  itemid?: number;
  typeid?: number;
  sort_type?: string;
  reason_shown?: string;
  rank?: number;
  feed_mode?: 'feed' | 'saved' | 'removed';
  sectionid?: number;
  section_typeid?: number;
  is_grouped?: 0 | 1;
}

export interface WatchPopupSaveBiEvent extends BiEvent {
  evid: 418;
  items_selected_count?: number;
}

export interface WatchPopupRemoveBiEvent extends BiEvent {
  evid: 419;
  items_selected_count?: number;
}

export interface WatchPopupCloseBiEvent extends BiEvent {
  evid: 420;
}

export interface WatchPopupSelectAllBiEvent extends BiEvent {
  evid: 421;
}

export interface WatchPopupSelectNoneBiEvent extends BiEvent {
  evid: 422;
}

export interface WatchPopupUndoRemovalBiEvent extends BiEvent {
  evid: 423;
  undo_action: string;
  is_grouped?: number;
  items_selected_count?: number;
  feed_mode?: string;
}

export interface WatchMenuItemSelectionBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 424;
  menu_item: string;
  is_grouped?: number;
  rank?: number;
}

export interface WatchBirthdaysOpenClickBiEvent extends BiEvent {
  evid: 425;
  view_mode?: 'feed' | 'saved' | 'removed';
}

export interface WatchSuggestionSelectBiEvent extends BiEvent {
  evid: 430;
}

export interface WatchSuggestionLoadMoreBiEvent extends BiEvent {
  evid: 431;
}

export interface WatchSuggestionWatchBiEvent extends BiEvent {
  evid: 432;
}

export interface WatchSuggestionSelectCancelBiEvent extends BiEvent {
  evid: 433;
}

export interface WatchSubFilterOpenBiEvent extends BiEvent {
  evid: 434;
  filter_name?: string;
}

export interface WatchSubFilterSelectBiEvent extends BiEvent {
  evid: 435;
  filter_name?: string;
}

export interface AddDiscountClickBiEvent extends BiEvent {
  evid: 450;
  sectionname?: string;
}

export interface CreateDiscountConfirmClickBiEvent extends BiEvent {
  evid: 451;
  campaignid: number | null;
  is_valid: 0 | 1;
}

export interface EditDiscountClickBiEvent extends BiEvent {
  evid: 452;
  campaignid: number;
}

export interface EndDiscountClickBiEvent extends BiEvent {
  evid: 453;
  campaignid: number;
}

export interface EndDiscountConfirmClickBiEvent extends BiEvent {
  evid: 454;
  campaignid: number;
}

export interface UpdateDiscountClickBiEvent extends BiEvent {
  evid: 455;
  campaignid: number;
  is_valid: 0 | 1;
}

export interface DuplicateDiscountClickBiEvent extends BiEvent {
  evid: 456;
  campaignid: number;
}

export interface ViewAvailableDiscountsClickBiEvent
  extends BiEvent,
    MixinSection,
    MixinItemIdAndTypeId {
  evid: 457;
  campaignid_list: string;
}

export interface MessageIconClickBiEvent
  extends BiEvent,
    Partial<MixinSection> {
  evid: 470;
  recipientid: string;
}

export interface NewMessageClickBiEvent extends BiEvent {
  evid: 471;
  sectionname: string;
}

export interface CreateChannelClickBiEvent extends BiEvent {
  evid: 472;
  sectionname: string;
}

export interface MessageSendClickBiEvent extends BiEvent {
  evid: 474;
  message_id?: string;
  room_id?: string;
  room_type?: string;
  uuid: string;
  message_format?: string;
  is_message_sent: 1 | 0;
  error_type?: string;
  is_edit?: 1 | 0;
  is_reply?: 1 | 0;
}

export interface MessageReactionAddClickBiEvent extends BiEvent {
  evid: 475;
  message_id: string;
  room_id: string;
  uuid: string;
  room_type: string;
  reaction_type_id: number;
  sectionname: string;
}

export interface MessageReactionRemoveClickBiEvent extends BiEvent {
  evid: 476;
  message_id: string;
  room_id: string;
  uuid: string;
  room_type: string;
  reaction_type_id: number;
  sectionname: string;
}

export interface MessageActionClickBiEvent extends BiEvent {
  evid: 477;
  message_id: string;
  uuid: string;
  room_id: string;
  room_type: string;
  action: string;
}

export interface MessageDeleteConfirmBiEvent extends BiEvent {
  evid: 478;
  message_id: string;
  uuid: string;
  room_id: string;
  room_type: string;
}

export interface MessageRoomActionClickBiEvent extends BiEvent {
  evid: 479;
  sectionname?: string;
  room_id: string;
  room_type: string;
  action: string;
}

export interface MessageRoomSelectBiEvent
  extends BiEvent,
    Partial<MixinSection> {
  evid: 483;
  room_id: string;
  room_type: string;
  has_draft: 1 | 0;
}

export interface MessageRoomActionConfirmBiEvent extends BiEvent {
  evid: 480;
  room_id: string;
  room_type: string;
  action: string;
  sectionname?: string;
}

export interface MessageRequestActionClickBiEvent extends BiEvent {
  evid: 482;
  room_id: string;
  room_type: string;
  action: 'accept' | 'delete' | 'block and delete';
  sectionname?: string;
}

export interface MessageConnectionErrorBiEvent extends BiEvent {
  evid: 484;
}

export interface ShowMoreMessagesBiEvent extends BiEvent {
  evid: 485;
  room_id: string;
  uuid: string;
  room_type: string;
  sectionname: string;
}

export interface MessageSearchBiEvent extends BiEvent {
  evid: 486;
  search_terms: string;
}

export interface MessageMenuClickBiEvent extends BiEvent {
  evid: 487;
  sectionname: string;
  menu_option: string;
}

export interface MessageCaptchaCheckEvent extends BiEvent {
  evid: 488;
  is_success: boolean;
  error_type: string;
}

export interface MessageRoomStarActionClickBiEvent extends BiEvent {
  evid: 481;
  room_id: string;
  room_type: string;
  action: 'star' | 'unstar';
  sectionname: string;
}

// #endregion
// #region 500-599
export interface BuyCoreButtonClickFromPPBiEvent extends BiEvent {
  evid: 500;
  referral_info: ReferralType;
  background_id?: number;
  give_core_or_become_core?: string;
  membershiplength?: number;
  membershiptypeid?: number;
}

export interface PackagePickerClickBiEvent extends BiEvent {
  evid: 501;
  button_type: 'gift' | 'manage' | 'notify' | 'select';
  core_type: 'core' | 'core_plus' | 'core_pro' | 'core_pro_plus' | 'core_max';
  give_core_or_become_core: 'upgrade_core' | 'give_core' | 'become_core';
}

export interface GetMorePointsBiEvent extends BiEvent {
  evid: 504;
  typeid: number;
  widgetname: string;
}

export interface CreditCardTokenizationBiEvent extends BiEvent {
  evid: 512;
  response_has_billing_token: 1 | 0;
  tokenization_http_response_code: number;
}

export interface PaymentButtonClickBiEvent extends BiEvent {
  evid: 520;
  payment_type: string;
  item_total: number;
  num_items: number;
  subproductid: string;
  give_core_or_become_core: string;
  is_form_valid: number;
  tipamount?: string;
}

export interface PaymentTypeClickBiEvent extends BiEvent {
  evid: 521;
  payment_type?: string;
  item_total?: number;
  num_items?: number;
}

export interface TipClickBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 522;
  tipamount: string | null;
  item_total: number;
  payment_type: string;
}

export interface GetMorePointsClickBiEvent extends BiEvent {
  evid: 525;
  payment_type?: string;
  item_total?: number;
  num_items?: number;
}

export interface CancelSubscriptionClickBiEvent extends BiEvent {
  evid: 526;
}

export interface CancelSubscriptionConfirmBiEvent extends BiEvent {
  evid: 527;
  orderid: number;
}

export interface TierAddNewBiEvent extends BiEvent {
  evid: 530;
  is_empty_state_view: number;
  widgetname?: string;
  is_tip_jar?: number;
}

export interface TierUploadCoverImageBiEvent extends BiEvent {
  evid: 531;
}

export interface TierAddContentBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 532;
  button_name: string;
}

export interface TierPreviewClickBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 533;
}

export interface TierPublishBiEvent extends BiEvent {
  evid: 535;
}

export interface TierEditClickBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 536;
}

export interface ArtistConnectToDiscordClickBiEvent extends BiEvent {
  evid: 537;
  referral_info: ReferralType;
}

export interface TierUpdateBiEvent extends BiEvent {
  evid: 538;
}

export interface TierSaveAsDraftBiEvent extends BiEvent {
  evid: 539;
}

export interface TierSaveAsDraftConfirmBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 540;
  display_subscribers: number;
  access_settings: string;
}

export interface TierDeleteBiEvent
  extends BiEvent,
    Partial<MixinItemIdAndTypeId> {
  evid: 541;
  has_subscribers: number;
}

export interface TierDeleteConfirmBiEvent extends BiEvent {
  evid: 542;
}

export interface TierNavigationSelectBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 543;
  authorid?: string;
}

export interface TierViewButtonBiEvent extends BiEvent {
  evid: 544;
  typeid: number;
  itemid: number;
}

export interface ViewTierUniqueBiEvent extends BiEvent {
  evid: 545;
  typeid: number;
  itemid: number;
  referral_info?: string;
  sectionid?: number;
  sectionname?: string;
  section_typeid?: number;
  sectiontype?: string;
  sectionrank?: number;
  rank?: number;
}

export interface ViewAdoptableTabBiEvent extends BiEvent {
  evid: 546;
  typeid: number;
  itemid: number;
  sectionname?: string;
  section_typeid?: number;
  referral_info: ReferralType;
}

export interface ViewSubscriptionTabBiEvent extends BiEvent {
  evid: 547;
  typeid: number;
  itemid: number;
  page_typeid?: number;
  page_itemid?: number;
  sectionname?: string;
  section_typeid?: number;
  referral_info: ReferralType;
}

export interface ViewCommissionTabBiEvent extends BiEvent {
  evid: 548;
  typeid: number;
  itemid: number;
  sectionname?: string;
  section_typeid?: number;
  referral_info: ReferralType;
}

export interface ViewPremiumGalleriesTabBiEvent extends BiEvent {
  evid: 549;
  typeid: number;
  itemid: number;
  sectionname?: string;
  section_typeid?: number;
  referral_info: ReferralType;
}

export interface TierSubscribeBiEvent extends BiEvent {
  evid: 550;
  typeid: number;
  itemid: number;
  referral_info: ReferralType;
}

export interface TierUnsubscribeBiEvent extends BiEvent {
  evid: 551;
  typeid: number;
  itemid: number;
  referral_info: ReferralType;
}

export interface TierUnsubscribeConfirmBiEvent
  extends BiEvent,
    MixinItemIdAndTypeId {
  evid: 552;
  referral_info: ReferralType;
}

export interface TierUpgradeBiEvent extends BiEvent, MixinItemIdAndTypeId {
  evid: 553;
  upgrade_type: string;
}

export interface SubscriptionHelpCenterBiEvent
  extends Omit<DefaultBiEvent, 'evid' | 'click_info'> {
  evid: 554;
  typeid: 82;
  tip_heading: string;
  variant_type: string;
}

export interface SubscriberConnectToDiscordClickBiEvent extends BiEvent {
  evid: 555;
  itemid: number;
}

export interface ViewShopPremiumDownloadsTabBiEvent extends BiEvent {
  evid: 557;
  typeid: number;
  itemid: number;
  sectionname?: string;
  section_typeid?: number;
  referral_info: ReferralType;
}

export interface SubscriptionBiEvent {
  subscription_type: 'core' | 'subscription';
  orderid?: number;
  subproductid?: number;
  cancel_reason_name?: string;
}

export interface ManageSubscriptionBiEvent
  extends SubscriptionBiEvent,
    BiEvent {
  evid: 560;
}

export interface CancelSubscriptionOneBiEvent
  extends SubscriptionBiEvent,
    BiEvent {
  evid: 561;
}

export interface CancelSubscriptionTwoBiEvent
  extends SubscriptionBiEvent,
    BiEvent {
  evid: 562;
}

export interface CancelSubscriptionThreeBiEvent
  extends SubscriptionBiEvent,
    BiEvent {
  evid: 563;
}

// #endregion
// #region 600-699
export interface MoreActionsGroupNotificationBiEvent extends BiEvent {
  evid: 605;
  notification_type: string;
  menu_item: string;
  items_selected_count?: number;
  sectionname?: string;
  is_grouped?: 0 | 1;
  groupid?: string | number;
  notification_sender_id?: string;
  feed_mode?: 'feed' | 'saved' | 'removed';
}

export interface ViewModeSelectBiEvent extends BiEvent {
  evid: 620;
}

export interface ExpandNotificationsBiEvent extends BiEvent {
  evid: 621;
  feed_mode?: 'feed' | 'saved' | 'removed';
  groupid?: string | number;
  is_grouped?: 0 | 1;
  sectionname?: string;
}

export interface ViewMoreNotificationsClickBiEvent extends BiEvent {
  evid: 623;
}

export interface NotificationsSectionSelectBiEvent extends BiEvent {
  evid: 624;
}

export interface NotificationsItemSaveBiEvent extends BiEvent {
  evid: 627;
  feed_mode?: 'feed' | 'saved' | 'removed';
  is_grouped?: 0 | 1;
  sectionname?: string;
  items_selected_count?: number;
  notification_sender_id?: string;
  notification_type?: string;
  groupid?: string | number;
}

export interface BeginTakedownProcessBiEvent extends BiEvent {
  evid: 628;
  deviationid?: number;
  notification_sender_id?: string;
  notification_item_id?: number;
  notification_type?: string;
  link_name?: string;
  link_url?: string;
}

export interface NotificationsOptOutBiEvent extends BiEvent {
  evid: 629;
  notification_type: string;
  deviationid: number;
  sectionname?: string;
  typeid?: number;
}

export interface NCBadgeCTAClickBiEvent extends BiEvent {
  evid: 630;
  cta_type: 'link' | 'button';
  badge_type: string;
  link_url: string;
  notification_type?: string;
  notification_message_id?: string;
}

export interface CommissionOrderAcceptedBiEvent extends BiEvent {
  evid: 633;
}

export interface CommissionOrderDeclinedBiEvent extends BiEvent {
  evid: 634;
}

export interface GroupInviteAcceptedBiEvent extends BiEvent {
  evid: 635;
}

export interface GroupInviteDeclinedBiEvent extends BiEvent {
  evid: 636;
}

export interface DeviationToGroupAcceptedBiEvent extends BiEvent {
  evid: 637;
}

export interface DeviationToGroupDeclinedBiEvent extends BiEvent {
  evid: 638;
}

export interface TagModelRequestConfirmedBiEvent extends BiEvent {
  evid: 639;
}

export interface TagModelRequestDeniedBiEvent extends BiEvent {
  evid: 640;
}

export interface ElninoViewBiEvent extends BiEvent {
  evid: 641;
}

export interface NotificationClickBiEvent extends BiEvent {
  evid: 650;
  notification_type: string;
  notification_sender_id?: string;
  notification_rank?: number;
  notification_type_id?: number;
  notification_item_id?: number;
  link_url?: string;
}

export interface NotificationGiveClickBiEvent extends BiEvent {
  evid: 651;
  notification_sender_id: string;
  notification_type: string;
}

export interface LiveNotificationToggleBiEvent extends BiEvent {
  evid: 652;
}

export interface LiveNotificationClickBiEvent extends BiEvent {
  evid: 653;
}

export interface LiveNotificationActivityOptionBiEvent extends BiEvent {
  evid: 654;
}

export interface LiveNotificationCancelBiEvent extends BiEvent {
  evid: 655;
  notification_type: string;
  notification_sender_id: string;
  typeid: number;
  itemid: number;
}

export interface LiveNotificationViewBiEvent extends BiEvent {
  evid: 656;
  notification_type: string;
  notification_sender_id: string;
  typeid: number;
  itemid: number;
}

export interface NotificationClickReviewMatchBiEvent extends BiEvent {
  evid: 660;
  deviationid?: number;
  notification_message_id?: string;
  sectionname?: string;
  feed_mode?: 'feed' | 'saved' | 'removed';
  link_url?: string;
}

export interface ClickVioalteIpNftBiEvent extends BiEvent {
  evid: 661;
  view?: string;
  deviationid?: number;
  moduleid?: number;
  link_url?: string;
}

export interface ClickNotVioalteIpNftBiEvent extends BiEvent {
  evid: 662;
  view?: string;
  deviationid?: number;
  moduleid?: number;
  link_url?: string;
}

export interface ClickReportNftBiEvent extends BiEvent {
  evid: 663;
  deviationid?: number;
  moduleid?: number;
  view?: string;
  link_url?: string;
}

export interface NcClickNftLinkBiEvent extends BiEvent {
  evid: 664;
  view?: string;
  component?: string;
  deviationid: number;
  notification_message_id: string;
  link_name: string;
  link_url?: string;
}

export interface ClickNftLinkCanonicalPageBiEvent extends BiEvent {
  evid: 665;
  deviationid?: number;
  moduleid?: number;
  view?: string;
  component?: string;
  link_url?: string;
  link_name?: string;
}

export interface ClickMintedThisNftBiEvent extends BiEvent {
  evid: 666;
  deviationid?: number;
  moduleid?: number;
  link_url?: string;
  view: string;
}

export interface ClickDmcaTakedownCanonicalPageBiEvent extends BiEvent {
  evid: 667;
  deviationid?: number;
  moduleid?: number;
  link_url?: string;
  view?: string;
  component?: string;
}

export interface ClickLinkNftToDeviationBiEvent extends BiEvent {
  evid: 668;
  deviationid?: number;
  moduleid?: number;
  link_url?: string;
  view?: string;
  component?: string;
}

export interface NewChatBiEvent extends BiEvent {
  evid: 670;
  notification_sender_id?: string;
  notification_type?: string;
  notification_message_id?: string;
}

export interface EnableFloatingChatBiEvent extends BiEvent {
  evid: 671;
}

export interface OnlineModeBiEvent extends BiEvent {
  evid: 673;
  online_mode: number;
}

export interface ChatMuteModeBiEvent extends BiEvent {
  evid: 674;
  mute_mode: number;
}

export interface ChatDropdownMenuBiEvent extends BiEvent {
  evid: 677;
  menu_item?: string;
}

export interface ChatSelectBiEvent extends BiEvent {
  evid: 678;
  chat_uuid: string;
}

export interface ChatMaxMinBiEvent extends BiEvent {
  evid: 679;
  menu_item: string;
  is_expanding?: number;
}

export interface ChatAddMediaBiEvent extends BiEvent {
  evid: 680;
}

export interface AddMediaTypeBiEvent extends BiEvent {
  evid: 681;
  media_type: string;
}

export interface ChatSendBiEvent extends BiEvent, MixinViewComponent {
  evid: 682;
  chat_uuid: string;
}

export interface AddUserToChatBiEvent extends BiEvent, MixinViewComponent {
  evid: 683;
  chat_uuid: string;
}

export interface AddToGroupButtonBiEvent extends BiEvent {
  evid: 684;
  chat_uuid: string;
  num_users_added: number;
  added_user_guids: string;
}

export interface ChatActionConfirmBiEvent extends BiEvent, MixinViewComponent {
  evid: 685;
  menu_item: string;
}

export interface SelectChatListBiEvent extends BiEvent {
  evid: 686;
  chat_list_type: string;
}

export interface SendChatButtonClickBiEvent extends BiEvent {
  evid: 688;
}

export interface DragDeviationToChatBiEvent extends BiEvent {
  evid: 689;
}

// #endregion
// #region 700-799
export interface TopNavClickBiEvent extends BiEvent {
  evid: 700;
  link_name?: string;
  link_url?: string;
  /** is a numeric indicator signalling that there is an indicator over the
   * link, available for watch, cart, notifications, notes - 1 if something
   * is there 0/null otherwise */
  indicator?: number;
}

export interface TopNavMenuClickBiEvent extends BiEvent {
  evid: 702;
  link_url?: string;
  menu_item?: string;
  menu_name?: string;
  toggle_value?: string;
}

export interface SubmitMenuOpenBiEvent extends BiEvent {
  evid: 703;
  sectionname: string;
}

export interface SubmitButtonClickBiEvent extends BiEvent {
  evid: 704;
  sectionname: string;
}

export interface SearchClickBiEvent extends BiEvent {
  evid: 710;
  search_terms: string;
  sectionname: string;
}

export interface FooterClickBiEvent extends BiEvent {
  evid: 720;
  link_name: string;
}

export interface GetAppBannerClickBiEvent extends BiEvent {
  evid: 730;
  banner_type: 'small' | 'whole_screen';
}

export interface DismissAppBannerClickBiEvent extends BiEvent {
  evid: 732;
  banner_type: 'small' | 'whole_screen';
}

export interface OfferForSaleClickBiEvent extends BiEvent {
  evid: 739;
  typeid?: number;
  itemid?: number;
  necoid?: number;
}

export interface SellDeviationClickBiEvent extends BiEvent {
  evid: 740;
  typeid?: number;
  itemid?: number;
  selection: 'adoptables' | 'adoptables_resale' | 'premium_downloads';
  items_count?: number;
  deviationid_list?: string; // comma-delineated list of deviationids
}

export interface SellAdoptablesConfirmClickBiEvent extends BiEvent {
  evid: 741;
  typeid?: number;
  itemid?: number;
  selection: 'adoptables' | 'premium_downloads';
  // if being sold at fixed_price, the amount being sold at
  purchase_price?: number;
  // if accepting offers the amount the offers start at
  offer?: number;
  // the duration chosen (1,2,3,4 or null if only fixed price)
  duration?: number;
  allow_resale?: boolean;
  necoid?: number;
  is_resale?: 1 | 0;
  items_count: number;
  step_num: number | null;
  supporters_exclusive?: boolean;
  deviationid_list?: string; // comma-delineated list of deviationids
}

export interface MessageAddMediaTypeClickBiEvent extends BiEvent {
  evid: 473;
  media_type: string;
  room_id: string;
}

export interface EditSaleAdoptablesClickBiEvent extends BiEvent {
  evid: 742;
  // deviationid being edited
  itemid: number;
  typeid: number;
  sectionname: string;
}

export interface UpdateSaleAdoptablesClickBiEvent extends BiEvent {
  evid: 743;
  // deviationid being edited
  itemid: number;
  typeid: number;
}

export interface EndSaleAdoptablesClickBiEvent extends BiEvent {
  evid: 744;
  // deviationid being edited
  itemid: number;
  typeid: number;
}

export interface EndSaleConfirmAdoptablesClickBiEvent extends BiEvent {
  evid: 745;
  typeid?: number;
  itemid?: number;
}

export interface ViewOffersAdoptablesClickBiEvent extends BiEvent {
  evid: 746;
  itemid: number;
  typeid: number;
  sectionname: string;
}

export interface AcceptOfferAdoptablesClickBiEvent extends BiEvent {
  evid: 747;
  itemid: number; // deviationid
  typeid: number;
  offer: number;
  offer_uuid: string;
  is_time_up: 1 | 0;
}

export interface SellPremiumDownloadClickBiEvent extends BiEvent {
  evid: 748;
  itemid: number;
  typeid: number;
  purchase_price: number;
  non_source_file: 1 | 0;
  items_count: number;
  step_num: number | null;
  deviationid_list?: string; // comma-delineated list of deviationids
}

export interface AdoptAdoptablesClickBiEvent extends BiEvent {
  evid: 750;
  typeid?: number;
  purchase_price: number;
  itemid: number;
  sectionname?: string;
  is_gift: 1 | 0;
}

export interface MakeOfferAdoptablesClickBiEvent extends BiEvent {
  evid: 751;
  typeid?: number;
  itemid: number;
  sectionname: string;
}

export interface MakeOfferConfirmAdoptablesClickBiEvent extends BiEvent {
  evid: 752;
  itemid: number;
  typeid: number;
  offer: number;
  has_note: 1 | 0;
}

export interface ManageOfferAdoptablesClickBiEvent extends BiEvent {
  evid: 753;
  typeid?: number;
  itemid: number;
  sectionname: string;
}

export interface UpdateOfferAdoptablesClickBiEvent extends BiEvent {
  evid: 754;
  typeid: number;
  itemid: number; // deviationid of the adoptable
  current_offer: number; // offer price
  offer: number; // new offer price
  has_note: 1 | 0; // if user typed something into note field
}

export interface WithdrawOfferAdoptablesClickBiEvent extends BiEvent {
  evid: 755;
  typeid: number;
  itemid: number; // deviationid of the adoptable
  current_offer: number; // offer price
}

export interface WithdrawOfferConfirmAdoptablesClickBiEvent extends BiEvent {
  evid: 756;
  typeid: number;
  itemid: number; // deviationid of the adoptable
  current_offer: number;
}

export interface GiveAdoptableGiftClickBiEvent extends BiEvent {
  evid: 757;
  typeid: number;
  itemid: number;
  purchase_price: number;
  sectionname?: string;
  is_seller_gift?: 0 | 1;
}

export interface GiveAdoptableGiftConfirmClickBiEvent extends BiEvent {
  evid: 758;
  typeid: number;
  itemid: number;
  sectionname?: string;
  is_seller_gift?: 0 | 1;
}

// #endregion
// #region 800-899
export interface ProfileStatsSidebarBiEvent extends BiEvent {
  evid: 830;
}

export interface StatsChangeLevelClickBiEvent extends BiEvent {
  evid: 831;
  time_period: string;
  sectionname?: string;
}

export interface StatsOverviewTabClickBiEvent extends BiEvent {
  evid: 832;
}

export interface StatsTableSortClickBiEvent extends BiEvent {
  evid: 833;
  stat_name: string;
  rank: number;
  time_period: string;
  is_viewing_same_option: number;
  sectionname: string;
}

export interface GroupProfileSubmitMenuClickBiEvent extends BiEvent {
  evid: 850;
  typeid: number;
  itemid: number;
  view?: string;
  component?: string;
}

export interface GroupProfileSubmitOptionClickBiEvent extends BiEvent {
  evid: 851;
  typeid: number;
  itemid: number;
  view?: string;
  component?: string;
  submit_type: 'submit_new' | 'add_existing' | 'recommend_existing' | 'journal';
}

// #endregion
// #region 900-999
export interface DAProtectUploadClickBiEvent extends BiEvent {
  evid: 901;
}

export interface DAProtectUploadBiEvent extends BiEvent {
  evid: 902;
  number_of_files: number;
  is_drag_and_drop: 1 | 0;
}

export interface DAProtectManagePlanClickBiEvent extends BiEvent {
  evid: 903;
}

export interface DAProtectNotificationsClickBiEvent extends BiEvent {
  evid: 904;
}

export interface DreamupSubmitAsDeviationBiEvent extends BiEvent {
  evid: 910;
  sectionname: string;
  deviationid: number;
  prompt: string;
}

export interface DreamupPromptClickBiEvent extends BiEvent {
  evid: 911;
}

export interface DreamupClickCreateBiEvent extends BiEvent {
  evid: 912;
  prompt: string;
  is_valid: boolean;
  image_similarity: number;
  aspect_ratio: string;
  prompt_weight: number;
  art_style: string;
  negative_prompt: string;
}

export interface DreamupDeleteClickBiEvent extends BiEvent {
  evid: 913;
  sectionname: string;
  deviationid: number;
  // This is only included because we push a modal that ends up pushing a second modal and
  // pass the component through context is not an option there.
  component?: string;
}

export interface CopyPromptClickBiEvent extends BiEvent {
  evid: 914;
  deviationid: number;
  prompt: string;
}

export interface CreateVariationsClickBiEvent extends BiEvent {
  evid: 915;
  deviationid: number;
  prompt: string;
}

export interface DreamupExploreCreationsClickBiEvent extends BiEvent {
  evid: 916;
}

export interface GetPointsDreamupClickBiEvent extends BiEvent {
  evid: 918;
  referral_info: string;
}

export interface PurchasePointsCardClickBiEvent extends BiEvent {
  evid: 919;
  points_total: number;
  subproductid: number;
}

export interface ClickMoreLessTopicClickBiEvent extends BiEvent {
  evid: 920;
  menu_item?: string;
  itemid?: number;
}

export interface OpenPromptModalBiEvent extends BiEvent {
  evid: 921;
  deviationid: number;
}

export interface DreamupPromptSettingsClickBiEvent extends BiEvent {
  evid: 922;
}

export interface DreamupInfoClickBiEvent extends BiEvent {
  evid: 923;
  typeid?: number;
  itemid?: number;
  sectionname?: string;
}

export interface DreamupAddImageClickBiEvent extends BiEvent {
  evid: 924;
}

export interface DreamupImageRightsConfirmClickBiEvent extends BiEvent {
  evid: 925;
}

export interface EditWithMuroBiEvent extends BiEvent {
  evid: 926;
  page_itemid?: number;
  page_typeid?: number;
  sectionname?: string;
  deviationid?: number;
}

export interface DreamupDownloadBiEvent extends BiEvent {
  evid: 927;
  prompt?: string;
  deviationid: number;
}

export interface DreamupUpscaleModalOpenBiEvent extends BiEvent {
  evid: 928;
  prompt?: string;
  deviationid: number;
}

export interface DreamupUpscaleConfirmBiEvent extends BiEvent {
  evid: 929;
  prompt?: string;
  deviationid: number;
  creativity?: number;
  component?: string;
}

export interface DreamupSuggestedPromptClick extends BiEvent {
  evid: 930;
  prompt?: string;
}

export interface MuroDreamupCall extends BiEvent {
  evid: 931;
  page_itemid: number;
  page_typeid: number;
  is_valid: boolean;
  prompt: string;
  draw_type?: string;
}

export interface MuroPostClick extends BiEvent {
  evid: 932;
  page_itemid: number;
  page_typeid: number;
  deviationid: number;
  features: string;
  action?: string;
  draw_type?: string;
}

export interface MuroMyDrawingsDeleteBiEvent extends BiEvent {
  evid: 933;
  deviationid: number;
  page_typeid: number;
  page_itemid: number;
}

export interface DreamupRemoveBackgroundOpenBiEvent extends BiEvent {
  evid: 940;
  prompt?: string;
  deviationid: number;
}

export interface DreamupRemoveBackgroundConfirmBiEvent extends BiEvent {
  evid: 941;
  prompt?: string;
  deviationid?: number;
  component?: string;
  tool?: string;
}

export enum StudioWithdrawalPaymentMethod {
  None = 0,
  Points = 1,
  Check = 2,
  PayPal = 3,
  BitPay = 4,
  PayPalPayouts = 5,
  Stripe = 6,
}

export interface StudioEarningsWithdrawalClickBiEvent extends BiEvent {
  evid: 159;
  payment_method: StudioWithdrawalPaymentMethod;
  process_step: 'home' | 'setup' | 'resume_connection' | 'withdraw';
}

export interface TestBiEvent extends BiEvent {
  evid: 999;

  [key: string]: any;
}

// #endregion
// #region to infinity and beyond
// #endregion
